import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { download, ExportButton, fileName, rangeLabel, tableSheet } from "./excel";
import SearchableSelect from "../SearchableSelect";
import CustomerSelect, { CustomerScopeFilters, customerLabel } from "../CustomerSelect";
import { useReceivableLookups } from "../receivables/common";
import { printTable } from "../../utils/printTable";

/** Products, warehouses and customers for the report filters, loaded only when the report has those filters. */
const useFilterSources = (filters) => {
  const [sources, setSources] = useState({ products: [], warehouses: [], customers: [] });
  const key = filters.join(",");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      try {
        if (key.includes("product")) next.products = (await api.get("/api/v1/products", { size: 2000 }))?.content || [];
        if (key.includes("warehouse")) next.warehouses = (await api.get("/api/v1/warehouses/all")) || [];
        if (key.includes("customer")) next.customers = (await api.getAll("/api/v1/customers"))?.content || [];
      } catch (error) {
        toast.error(error.message || "Failed to load filter lists");
      }
      if (!cancelled) setSources((prev) => ({ ...prev, ...next }));
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);
  return sources;
};

const formatCell = (value, column) => {
  if (value === null || value === undefined || value === "") return "-";
  if (column.format === "money") return Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (column.format === "signedMoney")
    return `${Number(value) > 0 ? "+" : ""}${Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (column.format === "qty") return Number(value).toLocaleString("en-IN", { maximumFractionDigits: 3 });
  return String(value);
};

/**
 * Generic list-shaped report: GET endpoint returning an array of rows, an optional date range,
 * and a fixed set of columns. Used for every report except GST and Profit & Loss, whose response
 * shape isn't a flat row list.
 */
const ReportTable = ({ title, subtitle, endpoint, columns, dateFilter = "optional", filters = [], dateLabels, rowStyle }) => {
  const requireDateRange = dateFilter === "required";
  const showDateFilter = dateFilter !== "none";
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // the date range the rows on screen were loaded with — what the export is labelled as
  const [applied, setApplied] = useState({ fromDate: "", toDate: "" });
  // product / warehouse / customer filters, when the report has them
  const sources = useFilterSources(filters);
  const [productId, setProductId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [customerId, setCustomerId] = useState("");
  // stock reports: value at the purchase price or the selling price
  const [priceBasis, setPriceBasis] = useState("PURCHASE");
  // salesman and beats, when the report has that filter
  const lookups = useReceivableLookups(filters.includes("salesmanBeats") ? ["salesmen", "beats"] : []);
  const [scope, setScope] = useState({ salesmanUserId: "", beatIds: [] });
  const filterText = () =>
    [
      productId ? `Product: ${sources.products.find((p) => String(p.id) === String(productId))?.name || productId}` : null,
      warehouseId ? `Warehouse: ${sources.warehouses.find((w) => String(w.id) === String(warehouseId))?.name || warehouseId}` : null,
      customerId ? `Customer: ${customerLabel(sources.customers.find((c) => String(c.id) === String(customerId))) || customerId}` : null,
      filters.includes("priceBasis") ? `Valued at: ${priceBasis === "SELLING" ? "Selling price" : "Purchase price"}` : null,
    ]
      .filter(Boolean)
      .join(", ");

  const exportExcel = () => {
    const name = title || "Report";
    const describe = [showDateFilter ? rangeLabel(applied.fromDate, applied.toDate) : "Current position", applied.filterText].filter(Boolean).join(" — ");
    download(fileName(name, showDateFilter ? rangeLabel(applied.fromDate, applied.toDate) : ""), [tableSheet(name, describe, columns, rows)]);
  };

  const printPdf = () =>
    printTable(title || "Report", [showDateFilter ? rangeLabel(applied.fromDate, applied.toDate) : "Current position", applied.filterText]
      .filter(Boolean).join(" — "), columns, rows, { totals: true });

  const load = async () => {
    if (requireDateRange && (!fromDate || !toDate)) {
      toast.error("Please select both from and to dates");
      return;
    }
    setLoading(true);
    try {
      const params = {
        ...(showDateFilter ? { fromDate: fromDate || undefined, toDate: toDate || undefined } : {}),
        productId: productId || undefined,
        warehouseId: warehouseId || undefined,
        customerId: customerId || undefined,
        salesmanUserId: scope.salesmanUserId || undefined,
        beatIds: scope.beatIds.length ? scope.beatIds.join(",") : undefined,
        priceBasis: filters.includes("priceBasis") ? priceBasis : undefined,
      };
      const data = await api.get(endpoint, params);
      setRows(Array.isArray(data) ? data : []);
      setApplied({ fromDate, toDate, filterText: filterText() });
      setLoaded(true);
    } catch (error) {
      toast.error(error.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!requireDateRange) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = columns.reduce((acc, column) => {
    if ((column.format === "money" || column.format === "qty" || column.format === "signedMoney") && column.total !== false) {
      acc[column.key] = rows.reduce((sum, row) => sum + (Number(row[column.key]) || 0), 0);
    }
    return acc;
  }, {});

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading report...</div>
          </div>
        </div>
      ) : null}

      {subtitle ? <div className="page-subtitle" style={{ marginBottom: "12px" }}>{subtitle}</div> : null}

      <div className="filters-bar">
        {showDateFilter ? (
          <>
            {dateLabels ? <span className="page-subtitle">{dateLabels[0]}</span> : null}
            <input type="date" onClick={(e) => e.target.showPicker?.()} value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
            {dateLabels ? <span className="page-subtitle">{dateLabels[1]}</span> : null}
            <input type="date" onClick={(e) => e.target.showPicker?.()} value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
          </>
        ) : null}
        {filters.includes("salesmanBeats") ? (
          <CustomerScopeFilters scope={scope} onChange={setScope} salesmen={lookups.salesmen} beats={lookups.beats} compact />
        ) : null}
        {filters.includes("priceBasis") ? (
          <select value={priceBasis} onChange={(e) => setPriceBasis(e.target.value)} aria-label="Value at">
            <option value="PURCHASE">Purchase Price</option>
            <option value="SELLING">Selling Price</option>
          </select>
        ) : null}
        {filters.includes("product") ? (
          <div style={{ minWidth: 240 }}>
            <SearchableSelect
              value={productId}
              onChange={setProductId}
              options={[{ value: "", label: "All products" }, ...sources.products.map((p) => ({ value: p.id, label: p.name, sublabel: p.skuCode }))]}
              placeholder="All products"
            />
          </div>
        ) : null}
        {filters.includes("warehouse") ? (
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} aria-label="Warehouse">
            <option value="">All (good) warehouses</option>
            {sources.warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
                {w.type === "BAD" ? " (bad)" : ""}
              </option>
            ))}
          </select>
        ) : null}
        {filters.includes("customer") ? (
          <div style={{ flex: "1 1 560px" }}>
            <CustomerSelect customers={sources.customers} value={customerId} onChange={setCustomerId} placeholder="All customers (code / name)" />
          </div>
        ) : null}
        {productId || warehouseId || customerId ? (
          <button
            className="secondary"
            onClick={() => {
              setProductId("");
              setWarehouseId("");
              setCustomerId("");
            }}
          >
            Clear filters
          </button>
        ) : null}
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : showDateFilter ? "Run Report" : "Refresh"}
        </button>
        <ExportButton onClick={exportExcel} disabled={loading || rows.length === 0} />
        <button className="secondary" onClick={printPdf} disabled={loading || rows.length === 0} title="Print, or save as PDF from the print dialog">
          Print / PDF
        </button>
      </div>

      {!loaded && requireDateRange ? (
        <div className="empty-state">
          <span className="empty-state-title">Select a date range and click Run Report</span>
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} style={{ textAlign: "center" }}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length}>
                    <div className="empty-state">
                      <span className="empty-state-title">No data for this {requireDateRange ? "period" : "report"}</span>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={`${row.id ?? row.invoiceId ?? row.customerId ?? row.productId ?? row.brandId ?? row.salesPersonId ?? ""}-${index}`}
                    style={rowStyle ? rowStyle(row) : undefined}>
                    {columns.map((column) => (
                      <td key={column.key} style={{ textAlign: "center" }}>
                        {formatCell(row[column.key], column)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            {rows.length > 0 ? (
              <tfoot>
                <tr>
                  {columns.map((column, index) => (
                    <td key={column.key} style={{ fontWeight: 700, textAlign: "center" }}>
                      {index === 0 ? "Total" : totals[column.key] !== undefined ? formatCell(totals[column.key], column) : ""}
                    </td>
                  ))}
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportTable;
